// ==========================================================================
// Custom Country Select - Searchable bottom-sheet modal for phone input
// Compatible with react-phone-number-input's countrySelectComponent API
// Uses a portal so the modal isn't constrained by PhoneInput's DOM tree.
// ==========================================================================

import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getCountryCallingCode } from 'libphonenumber-js';
import type { Country } from 'react-phone-number-input';

import { countryName, nameCollator } from '@/i18n/countryNames';

interface CountryOption {
  value?: string;
  label: string;
  divider?: boolean;
}

interface CountrySelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: CountryOption[];
  iconComponent: React.ComponentType<{ country: string; label: string }>;
}

export default function CountrySelect({
  value,
  onChange,
  options,
  iconComponent: Flag,
}: CountrySelectProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search input when modal opens
  useEffect(() => {
    if (open) {
      const id = setTimeout(() => searchRef.current?.focus(), 150);
      return () => clearTimeout(id);
    }
    setSearch('');
  }, [open]);

  // Get calling code safely
  const getDialCode = (countryCode: string): string => {
    try {
      return '+' + getCountryCallingCode(countryCode as Country);
    } catch {
      return '';
    }
  };

  // Country names come from the browser's locale data, so the list follows the
  // app language. Re-sorted per locale, since the incoming options are ordered
  // by their English names.
  const countries = useMemo(() => {
    const collator = nameCollator(i18n.language);

    return options
      .filter((o) => o.value && !o.divider)
      .map((o) => ({
        code: o.value!,
        englishLabel: o.label,
        name: countryName(o.value!, i18n.language, o.label),
      }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [options, i18n.language]);

  // Filter countries by search. Matches the English name too, so someone
  // typing "Ethiopia" on an Amharic device still finds it.
  const filtered = search.trim()
    ? countries.filter((o) => {
        const q = search.toLowerCase();
        return (
          o.name.toLowerCase().includes(q) ||
          o.englishLabel.toLowerCase().includes(q) ||
          o.code.toLowerCase().includes(q) ||
          getDialCode(o.code).includes(q)
        );
      })
    : countries;

  const handleSelect = (countryCode: string) => {
    onChange(countryCode);
    setOpen(false);
  };

  const dialCode = value ? getDialCode(value) : '';

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 shrink-0 pr-3 border-r border-gray-200"
      >
        {value && <Flag country={value} label="" />}
        {dialCode && <span className="text-sm font-medium text-gray-700">{dialCode}</span>}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="text-gray-400 ml-0.5">
          <path
            d="M1 1L5 5L9 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Modal — rendered via portal to escape PhoneInput's layout */}
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
            onClick={() => setOpen(false)}
          >
            <div
              className="w-full max-w-lg bg-white rounded-t-2xl max-h-[75vh] flex flex-col animate-slide-up"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-2">
                <h3 className="text-base font-semibold text-gray-900">
                  {t('countryPicker.title')}
                </h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-gray-400 text-lg font-bold p-1"
                >
                  ✕
                </button>
              </div>

              {/* Search input */}
              <div className="px-5 pb-3">
                <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2.5">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  <input
                    ref={searchRef}
                    type="text"
                    inputMode="search"
                    enterKeyHint="search"
                    autoComplete="off"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('countryPicker.searchPlaceholder')}
                    className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* Country list */}
              <div className="flex-1 overflow-y-auto px-2 pb-6">
                {filtered.length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-8">
                    {t('countryPicker.empty')}
                  </p>
                )}
                {filtered.map((option) => {
                  const code = option.code;
                  const dial = getDialCode(code);
                  const isSelected = code === value;

                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleSelect(code)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                        isSelected ? 'bg-gray-100' : 'active:bg-gray-50'
                      }`}
                    >
                      <Flag country={code} label={option.name} />
                      <span className="flex-1 text-sm text-gray-900 truncate">{option.name}</span>
                      <span className="text-sm text-gray-400 tabular-nums shrink-0">{dial}</span>
                      {isSelected && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="shrink-0"
                        >
                          <path
                            d="M3 8.5L6.5 12L13 5"
                            stroke="var(--brand-primary)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
